import { defineStore } from 'pinia'
import { ref } from 'vue'

import type { PlaygamaCTRContext } from '@/modules/analytics/types'

import { useLoggerStore } from './logger-store'

const IMPRESSION_VISIBILITY_DURATION_MS = 1000
const IMPRESSION_VISIBILITY_THRESHOLD = 0.5

export const useCtrStore = defineStore('ctr-observer', () => {
    const observable = ref<IntersectionObserver>()
    const loggerStore = useLoggerStore()

    const contextMap = new WeakMap<HTMLElement, PlaygamaCTRContext>()
    const observedElements = new Map<HTMLElement, ReturnType<typeof setTimeout> | undefined>()
    let hasVisibilityChangeListener = false

    function clearImpressionTimer(el: HTMLElement) {
        if (!observedElements.has(el)) {
            return
        }

        const timer = observedElements.get(el)
        if (timer !== undefined) {
            clearTimeout(timer)
        }
        observedElements.set(el, undefined)
    }

    function clearImpressionTimers() {
        observedElements.forEach((timer, element) => {
            if (timer !== undefined) {
                clearTimeout(timer)
            }
            observedElements.set(element, undefined)
        })
    }

    function onVisibilityChange() {
        if (document.visibilityState !== 'visible') {
            clearImpressionTimers()
            return
        }

        observedElements.forEach((_, element) => {
            observable.value?.unobserve(element)
            observable.value?.observe(element)
        })
    }

    const onClick = (event: MouseEvent) => {
        const element = event.currentTarget as HTMLElement

        const context = contextMap.get(element)
        if (context) {
            loggerStore.logCTREvents({ ...context, eventName: 'click' })
        }
    }

    const onIntersect: IntersectionObserverCallback = (entries) => {
        entries.forEach((entry) => {
            const element = entry.target as HTMLElement

            if (
                document.visibilityState !== 'visible' ||
                !entry.isIntersecting ||
                entry.intersectionRatio < IMPRESSION_VISIBILITY_THRESHOLD
            ) {
                clearImpressionTimer(element)
                return
            }

            // Ignore elements that are still loading/collapsed.
            if (entry.boundingClientRect.width === 0 || entry.boundingClientRect.height === 0) {
                return
            }

            if (!observedElements.has(element) || observedElements.get(element) !== undefined) {
                return
            }

            const timer = setTimeout(() => {
                if (observedElements.get(element) !== timer) {
                    return
                }

                observedElements.delete(element)

                if (document.visibilityState !== 'visible') {
                    return
                }

                observable.value?.unobserve(element)

                const context = contextMap.get(element)
                if (context) {
                    loggerStore.logCTREvents({ ...context, eventName: 'impression' })
                }
            }, IMPRESSION_VISIBILITY_DURATION_MS)

            observedElements.set(element, timer)
        })
    }

    function add(el: HTMLElement, ctrContext: PlaygamaCTRContext) {
        if (!observable.value) {
            observable.value = new IntersectionObserver(onIntersect, { threshold: IMPRESSION_VISIBILITY_THRESHOLD })
        }
        if (!hasVisibilityChangeListener && typeof document !== 'undefined') {
            document.addEventListener('visibilitychange', onVisibilityChange)
            hasVisibilityChangeListener = true
        }

        // remove el to update context
        if (contextMap.has(el)) {
            remove(el)
        }
        contextMap.set(el, ctrContext)
        observedElements.set(el, undefined)
        observable.value.observe(el)
        el.addEventListener('click', onClick)
    }

    function remove(el: HTMLElement) {
        clearImpressionTimer(el)
        observable.value?.unobserve(el)
        observedElements.delete(el)
        el.removeEventListener('click', onClick)
        contextMap.delete(el)
    }

    function disconnect() {
        clearImpressionTimers()
        observedElements.clear()
        if (hasVisibilityChangeListener && typeof document !== 'undefined') {
            document.removeEventListener('visibilitychange', onVisibilityChange)
            hasVisibilityChangeListener = false
        }
        observable.value?.disconnect()
        observable.value = undefined
    }

    return {
        add,
        remove,
        disconnect,
    }
})
